import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail, Heart, ArrowUp } from 'lucide-react';

export default function Footer({ socialMediaLinks, contactInfo }) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer id="contact" className="bg-slate-900 dark:bg-black text-white py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Main content */}
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* About */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-4 gradient-text">KG</h3>
            <p className="text-gray-400">
              Building exceptional software and sharing knowledge with the developer community.
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#home" className="hover:text-primary-400 transition-colors">Home</a></li>
              <li><a href="#projects" className="hover:text-primary-400 transition-colors">Projects</a></li>
              <li><a href="#education" className="hover:text-primary-400 transition-colors">Education</a></li>
              <li><a href="#contact" className="hover:text-primary-400 transition-colors">Contact</a></li>
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold mb-4">Connect</h4>
            <div className="space-y-3">
              {contactInfo && (
                <>
                  {contactInfo.email_address && (
                    <a
                      href={`mailto:${contactInfo.email_address}`}
                      className="flex items-center gap-2 text-gray-400 hover:text-primary-400 transition-colors"
                    >
                      <Mail size={18} />
                      {contactInfo.email_address}
                    </a>
                  )}
                  {contactInfo.number && (
                    <a
                      href={`tel:${contactInfo.number}`}
                      className="flex items-center gap-2 text-gray-400 hover:text-primary-400 transition-colors"
                    >
                      <span>📱</span>
                      {contactInfo.number}
                    </a>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 py-8 mb-8" />

        {/* Social Links */}
        <div className="flex justify-center gap-8 mb-8">
          {socialMediaLinks.github && (
            <motion.a
              whileHover={{ scale: 1.2, rotate: 12 }}
              href={socialMediaLinks.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-primary-400 transition-colors"
            >
              <Github size={24} />
            </motion.a>
          )}
          {socialMediaLinks.linkedin && (
            <motion.a
              whileHover={{ scale: 1.2, rotate: 12 }}
              href={socialMediaLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-primary-400 transition-colors"
            >
              <Linkedin size={24} />
            </motion.a>
          )}
          {socialMediaLinks.gmail && (
            <motion.a
              whileHover={{ scale: 1.2, rotate: 12 }}
              href={`mailto:${socialMediaLinks.gmail}`}
              className="text-gray-400 hover:text-primary-400 transition-colors"
            >
              <Mail size={24} />
            </motion.a>
          )}
        </div>

        {/* Copyright */}
        <div className="text-center text-gray-400 flex items-center justify-center gap-2">
          <p>
            © {new Date().getFullYear()} Khushal Gondaliya. Made with
            <Heart size={16} className="inline text-red-500 mx-1" />
            and lots of code.
          </p>
        </div>

        {/* Scroll to top button */}
        <motion.button
          onClick={scrollToTop}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-8 right-8 p-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full shadow-lg"
        >
          <ArrowUp size={24} />
        </motion.button>
      </div>
    </footer>
  );
}
